import { FunctionComponent, memo, useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addExamModel } from "../../Redux/Slices/ExamModel/AddExamModelSlice";
import { EditExamModel } from "../../Redux/Slices/ExamModel/EditExamModelSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { IExamModel } from "./IExamModel";
import { Button, ButtonGroup, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import {
  clearlistQuestionSubTypeResponse,
  listQuestionSubType,
} from "../../Redux/Slices/QuestionSubType/ListQuestionSubTypeSlice";
import NumberInputFieldlist from "../../Components/NumberInputlist/NumberInputFieldlist";
interface ExamModelFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IExamModel;
  setInitialValues: React.Dispatch<React.SetStateAction<IExamModel>>;
}
// Define the Zod schema with minimum value requirements
const userSchema = z.object({
  id: z.number().optional(),
  model_name:
   z.string().min(1, { message: "Please enter model name" })
     .max(50, { message:"model namecannot exceed 50 characters"}),
  questionTypes: z.any().optional(),
  question_counts: z.any().optional(),
  durations: z.any().optional(),
  sequence: z.any().optional(),
  subtypes: z.any().optional(),
  subtype_counts: z.any().optional(),
  subtype_sequences: z.any(),
  status: z.boolean({ message: "Please select status" }),
});
const ExamModelForm: FunctionComponent<ExamModelFormProps> = (props) => {
  const dispatch = useAppDispatch();
  // Fetching question types from Redux
  const { data: listQuestions } = useAppSelector(
    (state) => state.ListQuestionTypeSlice
  );

  const QuestionList: AutocompleteOption[] = useMemo(
    () =>
      Array.isArray(listQuestions?.data)
        ? listQuestions?.data
            ?.filter((role) => role.Status !== false)
            .map((listQuestions) => ({
              label: listQuestions?.name!,
              value: listQuestions.id,
            })) ?? []
        : [],
    [listQuestions?.data]
  );
  const { data: listQuestionssub } = useAppSelector(
    (state) => state.ListQuestionSubTypeSlice
  );
  const QuestionsubList: AutocompleteOption[] = Array.isArray(
    listQuestionssub?.data
  )
    ? listQuestionssub?.data
        ?.filter((role) => role.status !== false)
        .map((listQuestions) => ({
          label: listQuestions?.subtype_name!,
          value: listQuestions.id,
        })) ?? [] 
    : [];

  const methods = useForm<IExamModel>({
    defaultValues: {
      id: props.initialValues.id,
      model_name: props.initialValues.model_name || "",
      status: props.initialValues.id ? props.initialValues.status : true,
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });
  // State to store list of added entries
  const [displayDataListsub, setDisplayDataListsub] = useState<
    {
      subtypes?: { label: string; value: number } | null;
      questionTypes?: { label: string; value: number } | null;
      subtype_counts?: number | null;
      subtype_sequences?: number | null;
    }[]
  >([]);
  const [editingSub, setEditingSub] = useState<number | null>(null);
  // Add new data to the list
  const handleAddDatasub = () => {
    const questionTypes = methods.getValues("questionTypes");
    const subtypes = methods.getValues("subtypes");
    const subtype_counts = methods.getValues("subtype_counts");
    const subtype_sequences = methods.getValues("subtype_sequences");
    if (!questionTypes) {
      alert("Please select a question type first.");
      return;
    }
    const selectedQuestionTypesub = QuestionsubList?.find(
      (list) => list.value === subtypes
    );
    const selectedQuestionType = QuestionList?.find(
      (list) => list.value === questionTypes
    );
    if (!selectedQuestionType) {
      alert("Selected question type not found.");
      return;
    }
    if (!selectedQuestionTypesub) {
      alert("Selected subtype not found.");
      return;
    }
    const newItem = {
      questionTypes: selectedQuestionType,
      subtypes: selectedQuestionTypesub,
      subtype_counts,
      subtype_sequences,
    };
    if (editingSub !== null) {
      // Update existing
      setDisplayDataListsub((prevList: any) =>
        prevList.map((item: any, i: any) => (i === editingSub ? newItem : item))
      );
      setEditingSub(null);
    } else {
      // Check for duplicate
      const isDuplicatesub = displayDataListsub.some(
        (item) =>
          item.questionTypes?.value === questionTypes &&
          item.subtypes?.value === subtypes
      );
      if (isDuplicatesub) {
        alert("Question subtype combination already exists.");
        return;
      }
      // Add new
      setDisplayDataListsub((prevList: any) => [...prevList, newItem]);
    }
    // reset
    // methods.resetField("questionTypes");
    methods.resetField("subtype_counts", { defaultValue: "" });
    methods.resetField("subtypes");
    methods.resetField("subtype_sequences", { defaultValue: "" });
  };
  const handleEditSub = (index: number) => {
    const item = displayDataListsub[index];
    methods.setValue("questionTypes", item.questionTypes?.value);
    methods.setValue("subtypes", item.subtypes?.value);
    methods.setValue("subtype_counts", item.subtype_counts);
    methods.setValue("subtype_sequences", item.subtype_sequences);
    setEditingSub(index);
  };
  // Delete data by index
  const handleDeleteDatasub = (index: number) => {
    setDisplayDataListsub((prevList) => prevList.filter((_, i) => i !== index));
  };
  // State to store list of added entries
  const [displayDataList, setDisplayDataList] = useState<
    {
      questionTypes?: { label: string; value: number } | null;
      question_counts?: number | null;
      durations?: number | null;
      sequence?: number | null;
    }[]
  >([]);
  const [editingMain, setEditingMain] = useState<number | null>(null);
  // Add new data to the list
  const handleAddData = () => {
    const questionTypes = methods.getValues("questionTypes");
    const question_counts = methods.getValues("question_counts");
    const durations = methods.getValues("durations");
    const sequence = methods.getValues("sequence");
    const selectedQuestionType = QuestionList?.find(
      (list) => list.value === questionTypes
    );
    if (!selectedQuestionType) {
      alert("Selected question type not found.");
      return;
    }
    const newData: any = {
      questionTypes: selectedQuestionType,
      durations,
      sequence,
      question_counts,
    };
    if (editingMain !== null) {
      // Update existing
      setDisplayDataList((prevList: any) =>
        prevList.map((item: any, i: any) =>
          i === editingMain ? newData : item
        )
      );
      setEditingMain(null);
    } else {
      // Check if the selected question type already exists in the display data list
      const isDuplicate = displayDataList.some(
        (item) => item.questionTypes?.value === questionTypes
      );
      // If not duplicate and both values are present, add the new data
      if (isDuplicate) {
        alert("Question type already exists.");
        return;
      }
      if (questionTypes && question_counts && durations && sequence) {
        setDisplayDataList((prevList: any) => [...prevList, newData]);
      }
    }
    // reset
    methods.resetField("question_counts", { defaultValue: "" });
    // methods.resetField("questionTypes");
    methods.resetField("durations", { defaultValue: "" });
    methods.resetField("sequence", { defaultValue: "" });
  };
  const handleEditMain = (index: number) => {
    const item = displayDataList[index];
    methods.setValue("questionTypes", item.questionTypes?.value);
    methods.setValue("question_counts", item.question_counts);
    methods.setValue("durations", item.durations);
    methods.setValue("sequence", item.sequence);
    setEditingMain(index);
  };
  // Delete data by index
  const handleDeleteData = (questionTypeValue: any) => {
    // Remove from displayDataList
    setDisplayDataList((prevList) =>
      prevList.filter(
        (item: any) => item.questionTypes?.value !== questionTypeValue
      )
    );
    // Remove from displayDataListsub
    setDisplayDataListsub((prevList) =>
      prevList.filter(
        (item: any) => item.questionTypes?.value !== questionTypeValue
      )
    );
  };
  // Submit handler
  const QuestionTypes: any = displayDataList.map(
    (item: any) => item.questionTypes?.value
  );
  // Step 2: Create a map from groupedData
  const groupedDataMap = displayDataListsub.reduce((acc: any, item: any) => {
    const key = item.questionTypes?.value;
    if (key && !acc[key]) {
      acc[key] = { subtypes: [], subtype_counts: [], subtype_sequences: [] };
    }
    if (key) {
      acc[key].subtypes.push(item.subtypes?.value);
      acc[key].subtype_counts.push(item.subtype_counts);
      acc[key].subtype_sequences.push(item.subtype_sequences);
    }
    return acc;
  }, {});
  // Run validation
  const validationMessages = validateQuestionCounts(
    displayDataList,
    displayDataListsub
  );
  let missingTypes: any = [];
  function checkMissingTypes(mainList: any, subList: any) {
    const mainTypeValues = new Set(
      mainList.map((item: any) => item.questionTypes?.value).filter(Boolean)
    );
    missingTypes = []; // clear/reset it
    subList.forEach((sub: any) => {
      const type = sub.questionTypes;
      if (type?.value && !mainTypeValues.has(type.value)) {
        missingTypes.push(type.label);
      }
    });
  }
  // Call the function
  checkMissingTypes(displayDataList, displayDataListsub);
  // Use it outside
  if (missingTypes.length > 0) {
    console.log(`Please add the question types: ${missingTypes.join(", ")}`);
  }
  const onSubmit: SubmitHandler<IExamModel> = (val) => {
    if (validationMessages.some((msg: any) => msg.trim() === "")) {
      if (displayDataList.length >= 1 || displayDataListsub.length >= 1) {
        if (val.id) {
          const editalue: any = {
            id: val.id,
            model_name: val.model_name,
            questionTypes: displayDataList.map(
              (item) => item.questionTypes?.value
            ),
            question_counts: displayDataList.map(
              (item) => item.question_counts
            ),
            durations: displayDataList.map((item) => item.durations),
            sequence: displayDataList.map((item) => item.sequence),
            subtypes: QuestionTypes?.map(
              (qt: any) => groupedDataMap[qt]?.subtypes || []
            ),
            subtype_counts: QuestionTypes?.map(
              (qt: any) => groupedDataMap[qt]?.subtype_counts || []
            ),
            subtype_sequences: QuestionTypes.map(
              (qt: any) => groupedDataMap[qt]?.subtype_sequences || []
            ),
            status: val.status,
          };
          dispatch(EditExamModel(editalue));
        } else {
          const addvalue: any = {
            model_name: val.model_name,
            questionTypes: displayDataList.map(
              (item) => item.questionTypes?.value
            ),
            question_counts: displayDataList.map(
              (item) => item.question_counts
            ),
            durations: displayDataList.map((item) => item.durations),
            sequence: displayDataList.map((item) => item.sequence),
            subtypes: QuestionTypes?.map(
              (qt: any) => groupedDataMap[qt]?.subtypes || []
            ),
            subtype_counts: QuestionTypes?.map(
              (qt: any) => groupedDataMap[qt]?.subtype_counts || []
            ),
            subtype_sequences: QuestionTypes.map(
              (qt: any) => groupedDataMap[qt]?.subtype_sequences || []
            ),
            status: val.status,
          };
          dispatch(addExamModel(addvalue));
        }
      }
    }
  };
  const questionTypes = methods.getValues("questionTypes");
  const isDuplicate = displayDataList.some(
    (item) => item.questionTypes?.value === questionTypes
  );
  const questionTypessub = methods.getValues("subtypes");
  const isDuplicatesub = displayDataListsub.some(
    (item) =>
      item.subtypes?.value === questionTypessub &&
      item.questionTypes?.value === methods.getValues("questionTypes")
  );
  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, []);
  useEffect(() => {
    // Perform the effect for main list
    if (
      props.initialValues.id &&
      QuestionList?.length > 0 &&
      props.initialValues.questionTypes
    ) {
      const questionTypes: any = props.initialValues.questionTypes; // [10, 9, 13]
      const question_counts: any = props.initialValues.question_counts; // [11, 111, 2]
      const durations: any = props.initialValues.durations; // [11, 111, 2]
      const sequence: any = props.initialValues.sequence;
      // Map both arrays into the required format
      const mappedData = questionTypes?.map((typeValue: any, index: any) => {
        const selectedQuestionType = QuestionList?.find(
          (list) => list.value === typeValue
        );
        return {
          questionTypes: selectedQuestionType || {
            label: "Unknown",
            value: typeValue,
          },
          question_counts: question_counts[index] || null,
          durations: durations[index] || null,
          sequence: sequence[index] || null,
        };
      });
      setDisplayDataList(mappedData || []);
      // You can now use the mappedData for further logic if needed
    }
  }, [props.initialValues, QuestionList]);
  useEffect(() => {
    if (
      props.initialValues.id &&
      QuestionsubList?.length > 0 &&
      props.initialValues.subtypes
    ) {
      const subtypes: any = props.initialValues.subtypes; // [[5, 7], [6]]
      const questionTypes: any = props.initialValues.questionTypes; // [1, 2]
      const subtype_counts: any = props.initialValues.subtype_counts; // [[23, 21], [34]]
      const subtype_sequences: any = props.initialValues.subtype_sequences; // [[1, 12], [566666]]
      const mappedData = questionTypes?.map((typeValue: any, index: any) => {
        const selectedQuestionType = QuestionList?.find(
          (list) => list.value === typeValue
        );
        const mappedSubtypes = subtypes[index]?.map(
          (subtypeValue: any, subIndex: any) => {
            const selectedSubtype = QuestionsubList?.find(
              (sub) => sub?.value === subtypeValue
            );
            return {
              subtypes: selectedSubtype
                ? {
                    label: selectedSubtype.label,
                    value: selectedSubtype.value,
                  }
                : { label: "Unknown", value: subtypeValue },
              subtype_counts: subtype_counts[index]?.[subIndex] || null,
              subtype_sequences: subtype_sequences[index]?.[subIndex] || null,
            };
          }
        );
        return mappedSubtypes.map((subItem: any) => ({
          questionTypes: selectedQuestionType
            ? {
                label: selectedQuestionType.label,
                value: selectedQuestionType.value,
              }
            : { label: "Unknown", value: typeValue },
          ...subItem,
        }));
      });
      setDisplayDataListsub(mappedData.flat() || []);
    }
  }, [props.initialValues, QuestionsubList]);
  // Load subtypes for edit mode
  useEffect(() => {
    if (props.initialValues.id && props.initialValues.questionTypes) {
      const uniqueQTs = Array.from(
        new Set(props.initialValues.questionTypes as number[])
      );
      uniqueQTs.forEach((qt) => {
        dispatch(listQuestionSubType({ questiontype_id: qt }));
      });
    }
  }, [props.initialValues.id, props.initialValues.questionTypes]);
  // Status list
  const RoleSatus = [
    {
      label: "Active",
      value: true,
    },
    {
      label: "Inactive",
      value: false,
    },
  ];
  function validateQuestionCounts(
    displayDataList: any,
    displayDataListsub: any
  ) {
    let messages: any = [];
    // Get unique question type values from displayDataListsub
    const subQuestionTypes = new Set(
      displayDataListsub
        .map((subItem: any) => subItem.questionTypes?.value)
        .filter(Boolean)
    );
    displayDataList.forEach((mainItem: any) => {
      const mainValue = mainItem.questionTypes?.value;
      const mainCount = mainItem.question_counts;
      // Skip if no mainValue
      if (!mainValue) return;
      // Only validate if the question type exists in displayDataListsub
      if (!subQuestionTypes.has(mainValue)) {
        return;
      }
      // Find all matching sub-items
      const matchingSubItems = displayDataListsub.filter(
        (subItem: any) => subItem.questionTypes?.value === mainValue
      );
      // Sum all subtype counts for this question type
      const totalSubCount = matchingSubItems.reduce(
        (sum: any, subItem: any) => sum + subItem.subtype_counts,
        0
      );
      if (totalSubCount < mainCount) {
        messages.push(
          `Total question count across ${
            mainItem.questionTypes?.label
          } subtypes must match the required count for the question type
          Please add ${mainCount - totalSubCount} more.`
        );
      } else if (totalSubCount > mainCount) {
        messages.push(
          `Total question count across ${
            mainItem.questionTypes?.label
          } subtypes must match the required count for the question type
          Please remove ${totalSubCount - mainCount} to align.`
        );
      }
    });
    return messages.length > 0 ? messages : [""];
  }
  const FIlterdata: any = QuestionList?.filter((data) => {
    return data?.value === methods.getValues("questionTypes");
  });
  const isMainFieldsFilled =
    methods.watch("questionTypes") &&
    methods.watch("question_counts") &&
    methods.watch("durations") &&
    methods.watch("sequence");
  const isSubFieldsFilled =
    methods.watch("questionTypes") && // Added this check to disable if no question type selected
    methods.watch("subtypes") &&
    methods.watch("subtype_counts") &&
    methods.watch("subtype_sequences");
  const {
    formState: { errors },
  } = methods;
  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        // className="form_double_input"
        style={{ display: "grid", gap: "20px", marginTop: "10px" }}
      >
        {/* Input for Model Name */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <TextInputFieldC name="model_name" label="Model Name" type="text" />
         
        </div>
        <div className="twofieldlist">
          {/* Select for Question Type */}
          <div
            style={{ display: "flex", flexDirection: "column", width: "50%" }}
          >
            <Select
              name="questionTypes"
              label="Question Type"
              sx={{ width: "100%" }}
              options={QuestionList ?? []}
              value={
                QuestionList?.find(
                  (list) => list.value === methods.getValues("questionTypes")
                ) ?? null
              }
              onChange={(_, value) => {
                const val = value as AutocompleteOption;
                methods.setValue("questionTypes", val?.value ?? null, {
                  shouldValidate: true,
                });
                if (val?.value) {
                  dispatch(listQuestionSubType({ questiontype_id: val.value }));
                }
              }}
            />
            {errors.questionTypes && (
              <span
                style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
              >
                Please select a question type
              </span>
            )}
          </div>
          {/* {selected === "without Subtype" && ( */}
          <div
            style={{ display: "flex", flexDirection: "column", width: "25%" }}
          >
            <NumberInputFieldlist
              type="number"
              sx={{ width: "100%" }}
              name="question_counts"
              label="Question Count"
              inputProps={{ max: FIlterdata[0]?.label === "Writing" ? 1 : 100 }}
            />
            {errors.question_counts && (
              <span
                style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
              >
                Please enter question count
              </span>
            )}
          </div>
          {/* )} */}
          <div
            style={{ display: "flex", flexDirection: "column", width: "20%" }}
          >
            <NumberInputField
              type="number"
              sx={{ width: "100%" }}
              name="durations"
              label="Durations in Minutes"
            />
            {errors.durations && (
              <span
                style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
              >
                Please enter duration
              </span>
            )}
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", width: "20%" }}
          >
            <NumberInputField
              type="number"
              sx={{ width: "100%" }}
              name="sequence"
              label="Sequence"
            />
            {errors.sequence && (
              <span
                style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
              >
                Please enter sequence
              </span>
            )}
          </div>
          <AddCircleOutlineIcon
            titleAccess={
              editingMain !== null
                ? "Update this entry"
                : isDuplicate
                ? "Question type already exists. Please add another Question type"
                : !isMainFieldsFilled
                ? "Please fill all fields before adding."
                : ""
            }
            sx={{
              fontSize: "30px",
              color: "#009DFF",
              cursor: "pointer",
              opacity: editingMain !== null || isMainFieldsFilled ? 1 : 0.4, // visually disable if not filled
              pointerEvents:
                editingMain !== null || isMainFieldsFilled ? "auto" : "none", // disable click when incomplete
            }}
            onClick={() => {
              const questionType = methods.getValues("questionTypes");
              const questionCount = methods.getValues("question_counts");
              const duration = methods.getValues("durations");
              const seq = methods.getValues("sequence");
              if (questionType && questionCount && duration && seq) {
                handleAddData();
              } else {
                // optional toast or alert
                alert("Please fill all fields before adding.");
              }
            }}
          />
        </div>
        {missingTypes.length > 0 && (
          <div className="Altermeassge">
            <ReportGmailerrorredIcon
              fontSize="small"
              sx={{
                marginTop: "-1px",
                marginRight: "5px",
                color: "rgb(0 129 255)",
              }}
            />
            <div style={{ color: "rgb(0 129 255)" }}>
              Please add the question types: {missingTypes.join(", ")}
            </div>
          </div>
        )}
        {displayDataList.length >= 1 ? (
          ""
        ) : (
          <div className="Altermeassge">
            <ReportGmailerrorredIcon
              fontSize="small"
              sx={{
                marginTop: "-1px",
                marginRight: "5px",
                color: "rgb(0 129 255)",
              }}
            />
            <div style={{ color: "rgb(0 129 255)" }}>
              Please add question types and their corresponding question counts
              with duration
            </div>
          </div>
        )}
        {displayDataList.length > 0 && (
          <div
            style={{
              marginTop: "20px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(45%, 1fr))", // 2 columns
              gap: "10px",
            }}
          >
            {" "}
            {displayDataList.map((data, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                  backgroundColor: "lightgrey",
                  borderRadius: "5px",
                  padding: "5px",
                  // width: "50%",
                  boxShadow: "0px 3px 9px darkgray",
                }}
              >
                <div>
                  <b>{data.questionTypes?.label ?? "Unknown"}</b>
                  {/* {selected === "without Subtype" && ( */}
                  <div>
                    <b>Question Count :</b> {data.question_counts ?? "N/A"}
                  </div>
                  {/* )} */}
                  <div>
                    <b>Durations :</b> {data.durations ?? "N/A"} Minutes
                  </div>
                  <div>
                    <b>Sequence :</b> {data.sequence ?? "N/A"}{" "}
                  </div>
                </div>
                {/* Buttons for each entry */}
                <div style={{ display: "flex", gap: "5px" }}>
                  <IconButton
                    onClick={() => handleEditMain(index)}
                    size="small"
                  >
                    <EditIcon color="primary" fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteData(data?.questionTypes?.value)}
                    size="small"
                  >
                    <DeleteIcon color="error" fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="twofieldlist">
          {/* Select for Question Type */}
          {/* Select for Question Subtype */}
          <div
            style={{ display: "flex", flexDirection: "column", width: "50%" }}
          >
            <Select
              name="subtypes"
              label="Question Subtype"
              sx={{ width: "100%" }}
              options={QuestionsubList ?? []}
              disabled={
                (displayDataList?.length ?? 0) < 1 || // no question type added yet
                !methods.getValues("questionTypes") // no selected question type
              }
              value={
                QuestionsubList?.find(
                  (list) => list.value === methods.getValues("subtypes")
                ) ?? null
              }
              onChange={(_, value) => {
                const val = value as AutocompleteOption;
                methods.setValue("subtypes", val?.value ?? null, {
                  shouldValidate: true,
                });
              }}
            />
            {errors.subtypes && (
              <span
                style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
              >
                Please select a subtype
              </span>
            )}
          </div>
          {/* Input for Question Counts */}
          <div
            style={{ display: "flex", flexDirection: "column", width: "25%" }}
          >
            <NumberInputField
              type="number"
              // disabled={(QuestionsubList?.length ?? 0) < 1}
              disabled={(displayDataList?.length ?? 0) < 1}
              sx={{ width: "100%" }}
              name="subtype_counts"
              label="Question Subcount"
            />
            {errors.subtype_counts && (
              <span
                style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
              >
                Please enter subtype count
              </span>
            )}
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", width: "20%" }}
          >
            <NumberInputField
              type="number"
              // disabled={(QuestionsubList?.length ?? 0) < 1}
              disabled={(displayDataList?.length ?? 0) < 1}
              sx={{ width: "100%" }}
              name="subtype_sequences"
              label="Sequence"
            />
            {errors.subtype_sequences && (
              <span
                style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
              >
                Please enter subtype sequence
              </span>
            )}
          </div>
          {(displayDataList?.length ?? 0) < 1 ? (
            <AddCircleOutlineIcon sx={{ fontSize: "30px", color: "gray" }} />
          ) : (
            <AddCircleOutlineIcon
              titleAccess={
                editingSub !== null
                  ? "Update this subtype entry"
                  : isDuplicatesub === true
                  ? "Question Subtype already exists .Please add another Question Subtype"
                  : !isSubFieldsFilled
                  ? "Please fill all fields before adding."
                  : ""
              }
              sx={{
                fontSize: "30px",
                color: "#009DFF",
                cursor: "pointer",
                opacity: editingSub !== null || isSubFieldsFilled ? 1 : 0.4,
                pointerEvents:
                  editingSub !== null || isSubFieldsFilled ? "auto" : "none",
              }}
              onClick={() => {
                const subtypes = methods.getValues("subtypes");
                const subtype_counts = methods.getValues("subtype_counts");
                const subtype_sequences =
                  methods.getValues("subtype_sequences");
                if (subtypes && subtype_counts && subtype_sequences) {
                  handleAddDatasub();
                } else {
                  alert("Please fill all fields before adding.");
                }
              }}
            />
          )}
        </div>
        {validationMessages?.map((message: any, index: any) => (
          <div
            key={index}
            style={{
              marginBottom: "10px",
              color: "rgb(0 129 255)",
            }}
          >
            {message}
          </div>
        ))}
        {displayDataListsub.length > 0 && (
          <div
            style={{
              marginTop: "20px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(45%, 1fr))", // 2 columns
              gap: "10px",
            }}
          >
            {displayDataListsub.map((data, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                  backgroundColor: "lightgrey",
                  borderRadius: "5px",
                  padding: "5px",
                  // width: "50%",
                  boxShadow: "0px 3px 9px darkgray",
                }}
              >
                <div>
                  <div>
                    <b>QuestionType :</b>{" "}
                    {data?.questionTypes?.label ?? "Unknown"}
                  </div>
                  <div>
                    <b>Question Subtype :</b>{" "}
                    {data?.subtypes?.label ?? "Unknown"}
                  </div>
                  <div>
                    <b>Question Subcount :</b> {data?.subtype_counts ?? "N/A"}
                  </div>
                  <div>
                    <b>Sequence :</b> {data?.subtype_sequences ?? "N/A"}{" "}
                  </div>
                </div>
                {/* Buttons for each entry */}
                <div style={{ display: "flex", gap: "5px" }}>
                  <IconButton onClick={() => handleEditSub(index)} size="small">
                    <EditIcon color="primary" fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteDatasub(index)}
                    size="small"
                  >
                    <DeleteIcon color="error" fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Select
            name="status"
            label="Status"
            options={RoleSatus}
            value={
              RoleSatus?.find(
                (role) => role.value === methods.getValues("status")
              ) ?? null
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption;
              methods.setValue("status", val?.value, { shouldValidate: true });
            }}
          />
          {errors.status && (
            <span
              style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
            >
              {errors.status.message}
            </span>
          )}
        </div>
      </form>
    </FormProvider>
  );
};
export default memo(ExamModelForm);
